
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { YouTubeChannel, YouTubeChannelSelectionProps } from '../types';
import { useToast } from '@/components/ui/use-toast';

interface YouTubeProfileSelectionProps {
  channels: YouTubeChannel[];
  email: string;
  onSelectChannel: (channel: YouTubeChannel) => void;
  onCancel: () => void;
  onShowAllChannels: () => void;
  isLoading?: boolean;
  error?: string;
}

const YouTubeProfileSelection = ({
  channels,
  email,
  onSelectChannel,
  onCancel,
  onShowAllChannels,
  isLoading,
  error
}: YouTubeProfileSelectionProps) => {
  const { toast } = useToast();

  const handleCreateChannel = () => {
    // Utilisation d'une URL plus directe pour la création de chaîne
    window.open('https://www.youtube.com/create_channel', '_blank');
    
    toast({
      title: "YouTube Channel Required",
      description: "After creating your channel, please return to this page and try again.",
      duration: 8000,
    });
  };

  const handleRetry = () => {
    // Rafraîchir la page pour réessayer
    window.location.reload();
  }

  const isYouTubeSignupError = error?.includes('youtubeSignupRequired') || channels.length === 0;

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center">
        <Button variant="ghost" size="icon" onClick={onCancel} className="mr-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-medium">Add a YouTube channel</h2>
      </div>
      
      <div className="p-6">
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Select a YouTube channel to connect to your social profile.
          </p>
        </div>
        
        {isYouTubeSignupError ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="flex items-center justify-center bg-red-50 p-4 rounded-lg mb-4 text-red-600 w-full">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p className="text-sm font-medium">No YouTube channel found for your account</p>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              You need to create a YouTube channel before connecting it to SocialSync.
            </p>
            <div className="space-y-4 w-full max-w-md">
              <Button 
                onClick={handleCreateChannel}
                className="w-full"
              >
                Create YouTube Channel
              </Button>
              
              <Button 
                onClick={handleRetry}
                variant="outline"
                className="w-full"
              >
                I've Created a Channel - Try Again
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
              <h4 className="font-medium mb-2">How to create a YouTube channel:</h4>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Click the "Create YouTube Channel" button above</li>
                <li>Sign in with your Google account if prompted</li>
                <li>Follow YouTube's instructions to create your channel</li>
                <li>After creating your channel, return to this page</li>
                <li>Click "I've Created a Channel - Try Again" to continue</li>
              </ol>
            </div>
          </div>
        ) : channels.length === 1 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-gray-500 mb-4">You have one YouTube channel available:</p>
            <div 
              className="w-full border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectChannel(channels[0])}
            >
              <div className="flex items-center">
                {channels[0].thumbnailUrl && (
                  <img 
                    src={channels[0].thumbnailUrl}
                    alt={channels[0].title} 
                    className="w-12 h-12 rounded-full mr-4"
                  />
                )}
                <div>
                  <h3 className="text-base font-medium">{channels[0].title}</h3>
                  <p className="text-sm text-gray-500">
                    {channels[0].subscriberCount?.toLocaleString() || 0} subscribers
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-gray-500 mb-4">
              You have {channels.length} YouTube channels available. Select one to continue:
            </p>
            <Button 
              variant="outline" 
              onClick={onShowAllChannels}
              className="mb-4"
            >
              View all channels
            </Button>
            <div 
              className="w-full border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectChannel(channels[0])}
            >
              <div className="flex items-center">
                {channels[0].thumbnailUrl && (
                  <img 
                    src={channels[0].thumbnailUrl}
                    alt={channels[0].title} 
                    className="w-12 h-12 rounded-full mr-4"
                  />
                )}
                <div>
                  <h3 className="text-base font-medium">{channels[0].title}</h3>
                  <p className="text-sm text-gray-500">
                    {channels[0].subscriberCount?.toLocaleString() || 0} subscribers
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="pt-6 flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default YouTubeProfileSelection;
